import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { useI18n, type MessageKey } from '@/features/i18n/I18nProvider';
import { LocaleToggle } from '@/features/i18n/LocaleToggle';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { SubscriptionAlertBanner } from '@/features/subscription/SubscriptionAlertBanner';
import { RequireFeatureAccess } from '@/features/subscription/RequireFeatureAccess';
import { AdminIcon, type AdminIconName } from '@/features/admin/icons';
import { AnimatedPageTitle } from '@/features/ui/motion/AnimatedPageTitle';
import { PageTransition } from '@/features/ui/motion/PageTransition';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/admin/admin.css';
import './shell.css';
import '@/styles/user-ui.css';

type NavItem = {
  to: string;
  key: MessageKey;
  icon: AdminIconName;
  end?: boolean;
  /** Affiché dans la barre du bas (3 onglets + bouton Plus) */
  tab?: boolean;
  shortKey?: MessageKey;
};

type NavGroup = { labelKey: MessageKey; items: NavItem[] };

const userNavGroups: NavGroup[] = [
  {
    labelKey: 'nav.group.parcours',
    items: [
      { to: '/app', key: 'nav.dashboard', icon: 'layout', end: true, tab: true, shortKey: 'nav.short.dashboard' },
      { to: '/app/conception', key: 'nav.conception', icon: 'pen', tab: true, shortKey: 'nav.short.conception' },
      {
        to: '/app/planification',
        key: 'nav.planification',
        icon: 'calendar',
        tab: true,
        shortKey: 'nav.short.planification',
      },
      { to: '/app/pilotage', key: 'nav.pilotage', icon: 'chart', shortKey: 'nav.short.pilotage' },
    ],
  },
  {
    labelKey: 'nav.group.outils',
    items: [
      {
        to: '/app/marketplace-crm',
        key: 'nav.marketplaceCrm',
        icon: 'store',
        shortKey: 'nav.short.marketplaceCrm',
      },
      { to: '/app/reseau', key: 'nav.network', icon: 'globe' },
      { to: '/app/accompagnement', key: 'nav.accompagnement', icon: 'handshake' },
      { to: '/app/concours', key: 'nav.concours', icon: 'trophy' },
    ],
  },
  {
    labelKey: 'nav.group.compte',
    items: [
      { to: '/app/projets', key: 'nav.projects', icon: 'folder' },
      { to: '/app/assistance', key: 'nav.assistance', icon: 'lifebuoy' },
      { to: '/app/abonnement', key: 'nav.subscription', icon: 'credit' },
      { to: '/app/profil', key: 'nav.profile', icon: 'user' },
    ],
  },
];

const userLinksFlat = userNavGroups.flatMap((g) => g.items);
const userTabLinks = userLinksFlat.filter((l) => l.tab);
const userMoreLinks = userLinksFlat.filter((l) => !l.tab);

type AdminNavItem = {
  to: string;
  label: string;
  shortLabel: string;
  icon: AdminIconName;
  end?: boolean;
  tab?: boolean;
};

type AdminNavGroup = { label: string; items: AdminNavItem[] };

const adminNavGroups: AdminNavGroup[] = [
  {
    label: 'Pilotage',
    items: [
      { to: '/admin', label: 'Dashboard', shortLabel: 'Accueil', icon: 'layout', end: true, tab: true },
      { to: '/admin/audience', label: 'Audience', shortLabel: 'Audience', icon: 'chart', tab: true },
      { to: '/admin/exploitation', label: 'Exploitation', shortLabel: 'Exploit.', icon: 'folder', tab: true },
      { to: '/admin/agenda', label: 'Agenda', shortLabel: 'Agenda', icon: 'calendar' },
    ],
  },
  {
    label: 'Communauté',
    items: [
      { to: '/admin/utilisateurs', label: 'Utilisateurs', shortLabel: 'Users', icon: 'users' },
      { to: '/admin/membres', label: 'Membres', shortLabel: 'Membres', icon: 'shield' },
      { to: '/admin/accompagnement', label: 'Accompagnement', shortLabel: 'Accomp.', icon: 'handshake' },
      { to: '/admin/parrainage', label: 'Parrainage', shortLabel: 'Parrain', icon: 'gift' },
    ],
  },
  {
    label: 'Contenu',
    items: [
      { to: '/admin/concours', label: 'Concours', shortLabel: 'Concours', icon: 'trophy' },
      { to: '/admin/reseau', label: 'Réseau', shortLabel: 'Réseau', icon: 'globe' },
      { to: '/admin/marketplace', label: 'Marketplace', shortLabel: 'Market', icon: 'store' },
      { to: '/admin/communautes', label: 'Communautés ISBD', shortLabel: 'Commun.', icon: 'share' },
    ],
  },
  {
    label: 'Support',
    items: [
      { to: '/admin/assistance', label: 'Assistance', shortLabel: 'Assist.', icon: 'lifebuoy' },
      { to: '/admin/contact', label: 'Contact', shortLabel: 'Contact', icon: 'mail' },
    ],
  },
];

const adminLinksFlat = adminNavGroups.flatMap((g) => g.items);
const adminTabLinks = adminLinksFlat.filter((l) => l.tab);
const adminMoreLinks = adminLinksFlat.filter((l) => !l.tab);

function pathMatches(pathname: string, to: string, end?: boolean): boolean {
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

function adminPageTitle(pathname: string): string {
  const exact = adminLinksFlat.find((l) => pathMatches(pathname, l.to, l.end));
  if (exact) return exact.label;
  return 'Administration';
}

function userPageTitle(pathname: string, t: (key: MessageKey) => string): string {
  const exact = userLinksFlat.find((l) => pathMatches(pathname, l.to, l.end));
  return exact ? t(exact.key) : t('shell.eyebrow');
}

type MobileTabItem = {
  to: string;
  label: string;
  icon: AdminIconName;
  end?: boolean;
};

function MobileTabBar({
  primary,
  more,
  moreLabel,
  moreTitle,
}: {
  primary: MobileTabItem[];
  more: MobileTabItem[];
  moreLabel: string;
  moreTitle: string;
}) {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [moreOpen]);

  const moreActive = useMemo(
    () => more.some((l) => pathMatches(location.pathname, l.to, l.end)),
    [location.pathname, more],
  );

  return (
    <>
      <nav className="mobile-nav" aria-label="Navigation mobile">
        {primary.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) => `mobile-nav-item${isActive ? ' is-active' : ''}`}
          >
            <span className="mobile-nav-icon" aria-hidden>
              <AdminIcon name={l.icon} size={18} />
            </span>
            <span className="mobile-nav-label">{l.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`mobile-nav-item mobile-nav-more${moreActive || moreOpen ? ' is-active' : ''}`}
          aria-expanded={moreOpen}
          aria-controls="mobile-more-sheet"
          onClick={() => setMoreOpen(true)}
        >
          <span className="mobile-nav-icon" aria-hidden>
            <AdminIcon name="more" size={18} />
          </span>
          <span className="mobile-nav-label">{moreLabel}</span>
        </button>
      </nav>

      {moreOpen ? (
        <>
          <button
            type="button"
            className="mobile-more-backdrop"
            aria-label="Fermer"
            onClick={() => setMoreOpen(false)}
          />
          <div className="mobile-more-sheet" id="mobile-more-sheet" role="dialog" aria-label={moreTitle}>
            <div className="mobile-more-head">
              <p>{moreTitle}</p>
              <button type="button" className="mobile-more-close" onClick={() => setMoreOpen(false)} aria-label="Fermer">
                <AdminIcon name="x" size={18} />
              </button>
            </div>
            <div className="mobile-more-list">
              {more.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) => `mobile-more-link${isActive ? ' is-active' : ''}`}
                  onClick={() => setMoreOpen(false)}
                >
                  <span className="mobile-more-link-icon" aria-hidden>
                    <AdminIcon name={l.icon} size={18} />
                  </span>
                  <span>{l.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

export function UserShell() {
  const { t } = useI18n();
  const location = useLocation();
  const pageTitle = userPageTitle(location.pathname, t);

  const primaryTabs: MobileTabItem[] = userTabLinks.map((l) => ({
    to: l.to,
    label: t(l.shortKey ?? l.key),
    icon: l.icon,
    end: l.end,
  }));
  const moreTabs: MobileTabItem[] = userMoreLinks.map((l) => ({
    to: l.to,
    label: t(l.key),
    icon: l.icon,
    end: l.end,
  }));

  return (
    <div className="shell">
      <aside className="shell-aside" id="user-nav">
        <div className="shell-aside-top">
          <div className="brand-block">
            <img className="brand-logo" src="/logo-isbd.png" alt="ISBD" />
          </div>
          <p className="shell-space-label">{t('shell.eyebrow')}</p>
        </div>

        <nav className="shell-nav" aria-label="Navigation entrepreneur">
          {userNavGroups.map((group) => (
            <div key={group.labelKey} className="shell-nav-group">
              <p className="shell-nav-group-label">{t(group.labelKey)}</p>
              <div className="shell-nav-group-items">
                {group.items.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
                  >
                    <span className="nav-item-icon" aria-hidden>
                      <AdminIcon name={l.icon} size={18} />
                    </span>
                    <span className="nav-item-label">{t(l.key)}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="shell-main">
        <header className="shell-header shell-header-row">
          <div className="shell-header-text">
            <p className="eyebrow">{t('shell.eyebrow')}</p>
            <AnimatedPageTitle title={pageTitle} />
          </div>
          <div className="shell-header-tools">
            <NotificationBell />
          </div>
        </header>
        <main className="shell-content">
          <SubscriptionAlertBanner />
          <RequireFeatureAccess>
            <PageTransition>
              <Outlet />
            </PageTransition>
          </RequireFeatureAccess>
        </main>
      </div>

      <MobileTabBar
        primary={primaryTabs}
        more={moreTabs}
        moreLabel={t('nav.more')}
        moreTitle={t('nav.moreTitle')}
      />
    </div>
  );
}

export function AdminShell() {
  const { signOut } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const pageTitle = adminPageTitle(location.pathname);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.info('Vous êtes déconnecté.', 'Session');
    } catch {
      toast.error('Impossible de se déconnecter. Réessayez.');
    }
  };

  const primaryTabs: MobileTabItem[] = adminTabLinks.map((l) => ({
    to: l.to,
    label: l.shortLabel,
    icon: l.icon,
    end: l.end,
  }));
  const moreTabs: MobileTabItem[] = adminMoreLinks.map((l) => ({
    to: l.to,
    label: l.label,
    icon: l.icon,
    end: l.end,
  }));

  return (
    <div className="shell shell-admin">
      <aside className="shell-aside" id="admin-nav">
        <div className="shell-aside-top">
          <div className="brand-block">
            <img className="brand-logo" src="/logo-isbd.png" alt="ISBD" />
          </div>
          <p className="shell-space-label">Administration</p>
        </div>

        <nav className="shell-nav" aria-label="Navigation administration">
          {adminNavGroups.map((group) => (
            <div key={group.label} className="shell-nav-group">
              <p className="shell-nav-group-label">{group.label}</p>
              <div className="shell-nav-group-items">
                {group.items.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
                  >
                    <span className="nav-item-icon" aria-hidden>
                      <AdminIcon name={l.icon} size={18} />
                    </span>
                    <span className="nav-item-label">{l.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="shell-main">
        <header className="shell-header shell-header-row">
          <div className="shell-header-text">
            <p className="eyebrow">Pilotage plateforme</p>
            <AnimatedPageTitle title={pageTitle} />
          </div>
          <div className="shell-header-tools">
            <LocaleToggle />
            <NotificationBell />
            <button type="button" className="shell-signout shell-signout--header" onClick={() => void handleSignOut()}>
              <AdminIcon name="logout" size={16} />
              <span>Déconnexion</span>
            </button>
          </div>
        </header>
        <main className="shell-content">
          <PageTransition>
            <Outlet />
          </PageTransition>
        </main>
      </div>

      <MobileTabBar primary={primaryTabs} more={moreTabs} moreLabel="Plus" moreTitle="Autres rubriques" />
    </div>
  );
}
